import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle, Shield, X, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function ConsentBanner() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [bannerType, setBannerType] = useState('missing'); // 'missing' | 'version_update'
  const [message, setMessage] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  // Re-check after the login consent prompt saves, so the banner never keeps
  // showing "pending" for consents the user just granted (docs/41 P0-1).
  useEffect(() => {
    const onUpdated = () => { setShow(false); setRefreshTick(t => t + 1); };
    window.addEventListener('consents-updated', onUpdated);
    return () => window.removeEventListener('consents-updated', onUpdated);
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    const checkConsent = async () => {
      try {
        const [statusRes, textsRes, settingsRes] = await Promise.all([
          api.get(`/consent/status/${user.id}`),
          api.get('/consent/texts').catch(() => ({ data: [] })),
          api.get('/consent-settings').catch(() => ({ data: [] })),
        ]);

        const beneficiaries = statusRes.data.beneficiaries || {};
        const latestTexts = textsRes.data || [];
        const consentSettingsData = settingsRes.data || [];

        // Determine which purposes are visible
        const visiblePurposes = new Set(
          consentSettingsData.length > 0
            ? consentSettingsData.filter(s => s.visible).map(s => s.purpose_id)
            : ['P1', 'P2', 'P3', 'P4']
        );

        // /consent/texts serves { version_id: "v1.0-P1", ... } — the same
        // format the grant handler stamps onto consent_text_version — NOT
        // { version } (review 2026-07-14: reading t.version left the map
        // undefined, so the version-mismatch banner never fired). Compare the
        // stored consent_text_version against version_id directly.
        const latestVersionMap = {};
        for (const t of latestTexts) {
          const pid = t.purpose_id;
          if (!latestVersionMap[pid] || t.version_id > latestVersionMap[pid]) {
            latestVersionMap[pid] = t.version_id;
          }
        }

        const missingPurposes = new Set();
        let versionMismatch = false;

        for (const [, purposes] of Object.entries(beneficiaries)) {
          for (const pid of ['P1', 'P2', 'P3', 'P4']) {
            if (!visiblePurposes.has(pid)) continue;
            const consent = purposes[pid];
            if (!consent || consent.status === 'PENDING') {
              missingPurposes.add(pid);
            } else if (consent.status === 'GRANTED' && latestVersionMap[pid]) {
              if (consent.consent_text_version && consent.consent_text_version < latestVersionMap[pid]) {
                versionMismatch = true;
              }
            }
          }
        }

        if (Object.keys(beneficiaries).length > 0) {
          if (missingPurposes.size > 0) {
            setMessage('Review your privacy settings — some consent preferences are pending.');
            setBannerType('missing');
            setShow(true);
          } else if (versionMismatch) {
            setMessage('Our privacy terms have been updated. Please review and re-confirm your consent choices.');
            setBannerType('version_update');
            setShow(true);
          }
        }
      } catch {
        // Silently fail
      }
    };

    checkConsent();
  }, [api, user, refreshTick]);

  if (!show) return null;

  const isVersionUpdate = bannerType === 'version_update';

  return (
    <div data-testid="consent-banner" className="mx-4 mt-3 rounded-xl border p-3 flex items-center gap-3 animate-in slide-in-from-top"
      style={{ background: isVersionUpdate ? '#EFF6FF' : '#FEF3C7', borderColor: isVersionUpdate ? '#93C5FD' : '#FCD34D' }}>
      {isVersionUpdate ? (
        <RefreshCw className="w-5 h-5 flex-shrink-0 text-blue-600" />
      ) : (
        <Shield className="w-5 h-5 flex-shrink-0 text-amber-600" />
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${isVersionUpdate ? 'text-blue-800' : 'text-amber-800'}`}>
          {message}
        </p>
        <button
          data-testid="consent-banner-action"
          onClick={() => { navigate('/settings/privacy'); setShow(false); }}
          className={`text-xs font-semibold underline underline-offset-2 mt-0.5 ${isVersionUpdate ? 'text-blue-700 hover:text-blue-900' : 'text-amber-700 hover:text-amber-900'}`}>
          Go to Privacy Settings
        </button>
      </div>
      <button onClick={() => setShow(false)} className={`flex-shrink-0 ${isVersionUpdate ? 'text-blue-400 hover:text-blue-600' : 'text-amber-500 hover:text-amber-700'}`}>
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
